import API_URL from './api';
import { useState, useEffect } from 'react';
import copyIcon from './copy-icon.png';
import './App.css';
import FolderPicker from './FolderPicker';

function Dashboard({ userData }) {

  const [pins, setPins] = useState([]);
  const [folders, setFolders] = useState([]);
  const [copied, setCopied] = useState(false);
  const [movingPinId, setMovingPinId] = useState(null);
  const [selectedPins, setSelectedPins] = useState([]);
  const [bulkFolderId, setBulkFolderId] = useState('');
  const [manualUrl, setManualUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  useEffect(() => {
    fetchPins();
    fetchFolders();
    const interval = setInterval(() => {
      fetchPins();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  async function fetchPins() {
    try {
      const res = await fetch(`${API_URL}/api/pins`, {
        headers: { Authorization: 'Bearer ' + userData.token },
      });
      const data = await res.json();
      setPins((data.pins || []).filter(p => !p.folder_id));
    } catch (err) {
      console.error('Could not fetch pins:', err);
    }
  }

  async function fetchFolders() {
    try {
      const res = await fetch(`${API_URL}/api/folders`, {
        headers: { Authorization: 'Bearer ' + userData.token },
      });
      const data = await res.json();
      setFolders(data.folders || []);
    } catch (err) {
      console.error('Could not fetch folders:', err);
    }
  }

  async function submitManualUrl() {
    if (!manualUrl) return;
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            Recipients: [userData.address],
            From: { Address: 'manual@thepinbook.com' },
            RawTextBody: manualUrl,
            Subject: ''
          }]
        }),
      });
      const data = await res.json();
      if (data.limitReached) {
        alert(data.message);
      } else {
        setManualUrl('');
        setTimeout(() => fetchPins(), 3000);
      }
    } catch (err) {
      console.error('Could not submit URL:', err);
    }
    setIsSubmitting(false);
  }

  async function moveToFolder(pinId, folderId) {
    try {
      await fetch(`${API_URL}/api/pins/${pinId}/folder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + userData.token,
        },
        body: JSON.stringify({ folderId }),
      });
      setMovingPinId(null);
      fetchPins();
    } catch (err) {
      console.error('Could not move pin:', err);
    }
  }

  async function deletePin(pinId) {
    if (!window.confirm('Delete this pin? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/pins/${pinId}`, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + userData.token },
      });
      if (res.ok) {
        await fetchPins();
      } else {
        console.error('Delete failed:', res.status);
      }
    } catch (err) {
      console.error('Could not delete pin:', err);
    }
  }

  async function bulkMove() {
    if (!bulkFolderId || selectedPins.length === 0) return;
    await Promise.all(selectedPins.map(pinId =>
      fetch(`${API_URL}/api/pins/${pinId}/folder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + userData.token,
        },
        body: JSON.stringify({ folderId: bulkFolderId }),
      })
    ));
    setSelectedPins([]);
    setBulkFolderId('');
    fetchPins();
  }

  function toggleSelect(pinId) {
    setSelectedPins(prev =>
      prev.includes(pinId) ? prev.filter(id => id !== pinId) : [...prev, pinId]
    );
  }

  function copyAddress() {
    navigator.clipboard.writeText(userData.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const handle = userData.address.split('@')[0];
  const capitalizedHandle = handle.charAt(0).toUpperCase() + handle.slice(1);

  return (
    <div className="dashboard">
      <p className="dashboard-welcome">Welcome, {capitalizedHandle}!</p>
      <div className="address-bar">
        <span className="address-bar-label">Your PinBook address:</span>
        <span className="address-bar-text">{userData.address}</span>
        <button className="copy-icon-btn" onClick={copyAddress} title="Copy address">
          {copied
            ? <span style={{ fontSize: '12px', color: '#27ae60' }}>Copied!</span>
            : <img src={copyIcon} className="copy-icon-img" alt="Copy" />
          }
        </button>
      </div>

      <div className="manual-url-box">
        <input
          type="text"
          placeholder="Paste a public social media URL to save as a pin..."
          value={manualUrl}
          onChange={(e) => setManualUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submitManualUrl()}
        />
        <button onClick={submitManualUrl} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save pin'}
        </button>
      </div>

      <div className="pins-section">
        <div className="inbox-header">
          <div className="inbox-title-row">
            <h3>📥 Inbox ({pins.length})</h3>
            <span className="inbox-note">Pins here have not been assigned to a chapter yet.</span>
          </div>
          <div className="select-all-row">
            <input
              type="checkbox"
              id="select-all"
              className="pin-checkbox"
              checked={selectedPins.length === pins.length && pins.length > 0}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedPins(pins.map(p => p.id));
                } else {
                  setSelectedPins([]);
                }
              }}
            />
            <label htmlFor="select-all" className="select-all-label">
              {selectedPins.length === pins.length && pins.length > 0 ? 'Deselect All' : 'Select All'}
            </label>
          </div>

          {selectedPins.length > 0 && (
            <div className="bulk-move">
              <span>{selectedPins.length} selected</span>
              <select
                value={bulkFolderId}
                onChange={(e) => setBulkFolderId(e.target.value)}
              >
                <option value="" disabled>Move to chapter...</option>
                {folders.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              <button className="move-btn" onClick={bulkMove}>Move</button>
              <button className="cancel-btn" onClick={() => setSelectedPins([])}>Cancel</button>
            </div>
          )}

          {pins.length === 0 && (
            <p className="empty">Your inbox is empty. Email a URL to your pinbook address to get started.</p>
          )}

          {pins.map((pin) => (
            <div className={`pin-card ${selectedPins.includes(pin.id) ? 'selected' : ''}`} key={pin.id}>
              <div className="pin-card-top">
                <input
                  type="checkbox"
                  checked={selectedPins.includes(pin.id)}
                  onChange={() => toggleSelect(pin.id)}
                  className="pin-checkbox"
                />
                <div className="pin-card-content">

                  <a className="pin-title-link"
                    href={pin.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {pin.title || pin.url}
                  </a>
                  <p className="pin-platform">{pin.platform}</p>
                  {pin.description && <p className="pin-description">{pin.description}</p>}
                  <p className="pin-transcript">{pin.transcript}</p>
                  <p className="pin-date">{new Date(pin.saved_at).toLocaleDateString()} {new Date(pin.saved_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
              {movingPinId === pin.id ? (
                <FolderPicker
                  pinId={pin.id}
                  folders={folders}
                  userData={userData}
                  onMove={(pinId, folderId) => moveToFolder(pinId, folderId)}
                  onCancel={() => setMovingPinId(null)}
                  onFolderCreated={fetchFolders}
                />
              ) : (
                <div className="pin-actions-row">
                  <button className="move-btn" onClick={() => setMovingPinId(pin.id)}>
                    Add to chapter
                  </button>
                  <button className="delete-btn" onClick={() => deletePin(pin.id)}><span className="trash-icon">🗑️</span></button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div >
  );
}

export default Dashboard;